import { CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";

import { KeepoLoader } from "../../components/KeepoLoader";
import { LoadingModal } from "../../components/LoadingModal";
import { useAppState } from "../../contexts/App";
import { useQueryUrl } from "../../hooks/QueryUrl";
import ChevronLeftIcon from "../../icons/ChevronLeft";
import ChevronRightIcon from "../../icons/ChevronRight";
import { GlobeIcon } from "../../icons/Globe";
import { StripeIcon } from "../../icons/Stripe";
import { api, hasActiveSubscription } from "../../services/api";
import { isApiError } from "../../services/api/errors";
import {
  confirmDomainPayment,
  createDomainOrder,
  cyclesOf,
  DOMAIN_ORDER_ERRORS,
  DomainOrder,
  DomainPrice,
  searchDomains,
} from "../../services/domains";
import { formatDate as formatLocaleDate } from "../../services/i18ln/format";
import { formatMoney } from "../../utils/money";
import { formatPhone } from "../../utils/phone";
import {
  BillingDetails,
  BillingDetailsValue,
  BillingErrors,
  EMPTY_BILLING,
  Field,
  validateBilling,
  withSavedBilling,
} from "../Subscription/pages/Checkout/BillingDetails";
import { LockIcon } from "../Subscription/pages/Checkout/icons";
import { PaymentMethod, PaymentMethods } from "../Subscription/pages/Checkout/PaymentMethods";
import { CardErrors, EMPTY_CARD, StripeProvider, validateCard } from "../Subscription/pages/Checkout/StripeCard";
import { Container, Header, Layout, Lock, Main, Submit, Summary } from "../Subscription/pages/Checkout/styles";
import { WalletAvailability, WalletPaymentButton, WalletProbe } from "../Subscription/pages/Checkout/WalletButton";
import { TopLinks } from "../Subscription/pages/Features/styles";
import { Panel } from "../Subscription/pages/Manage/styles";
import { Content } from "../Subscription/pages/PaymentPending/styles";
import { CycleOptions } from "./CycleOptions";
import { PixPayment } from "./PixPayment";
import { FireCta } from "./styles";

export const DomainCheckoutPage = () => (
  <StripeProvider>
    <DomainCheckout />
  </StripeProvider>
);

/**
 * Buying a domain, on the PLUS checkout's pieces: one year (once), privacy included, the Keepo to connect it to
 * (a domain of your own is PLUS), the owner (registering asks for them: the billing details saved, plus e-mail
 * and phone) and card, Apple Pay / Google Pay (where the device has them) or Pix (no boleto: it takes days,
 * while the name can be taken). The name is checked again
 * by the API before charging; it's registered once the payment arrives (refunded if that fails).
 */
const DomainCheckout = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { appState } = useAppState();
  const query = useQueryUrl();
  const domain = (query.get("domain") || "").toLowerCase();
  // The cycle picked in the search (1, 2 or 5 years), changeable here
  const [years, setYears] = useState(Number(query.get("years")) || 1);
  // Bought from a Keepo (its custom domain screen): connected to it once registered, if it's one of the
  // person's PLUS Keepos (otherwise it's just ignored)
  const site = query.get("site");
  const [targetKeepo, setTargetKeepo] = useState<{ siteId: string; url: string }>();
  const prefix = "pages.domains.checkout";
  const checkoutPrefix = "pages.subscription.checkout";

  // undefined: loading; null: not available; "error": couldn't check (try again)
  const [price, setPrice] = useState<DomainPrice | null | "error">();
  const [isSummaryOpen, setSummaryOpen] = useState(false);
  const [owner, setOwner] = useState<BillingDetailsValue>(EMPTY_BILLING);
  const [ownerErrors, setOwnerErrors] = useState<BillingErrors>({});
  const [contact, setContact] = useState({ email: "", phone: "" });
  const [contactErrors, setContactErrors] = useState<{ email?: boolean; phone?: boolean }>({});
  const [method, setMethod] = useState<PaymentMethod>();
  const [isMethodMissing, setMethodMissing] = useState(false);
  const [card, setCard] = useState(EMPTY_CARD);
  const [cardErrors, setCardErrors] = useState<CardErrors>({});
  const [isSubmitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const [pixOrder, setPixOrder] = useState<DomainOrder>();
  const [availableWallets, setAvailableWallets] = useState<WalletAvailability>({ applePay: false, googlePay: false });
  const isWallet = method === "applePay" || method === "googlePay";

  // The price (and that it's still free) and the details saved on the account (the Keepo is connected after,
  // on the domain's page)
  const loadPrice = () => {
    setPrice(undefined);
    searchDomains(domain)
      .then(({ exact }) => setPrice(exact.available && exact.price ? exact.price : null))
      .catch(() => setPrice("error"));
  };

  useEffect(() => {
    if (!domain) {
      return;
    }
    loadPrice();
    if (site) {
      api
        .getSites()
        .then(sites => {
          const found = sites.find(item => item.siteId === site && hasActiveSubscription(item.subscription));
          const data = found && (found.site || found.draft);
          setTargetKeepo(found && data?.url ? { siteId: found.siteId, url: data.url } : undefined);
        })
        .catch(() => undefined);
    }
    api
      .getUser()
      .then(user => {
        setOwner(current => withSavedBilling(current, user.billing, user.name));
        setContact(current => ({
          email: current.email || user.email || "",
          phone: current.phone || user.phone || "",
        }));
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain]);

  // Paid: the API hears it now (not only from the webhook, which may be late), then My domains shows it being
  // registered. If the confirmation fails, the webhook still does it
  const finish = async () => {
    await confirmDomainPayment(domain).catch(() => undefined);
    // Bought from a Keepo's custom domain screen: back there, where it shows being registered and connected
    // (the custom domain screen is for the Keepo being edited: another one goes to My domains)
    // (by the address, not by the Keepos loaded: coming back from a bank's page they aren't loaded yet; the API already
    // checked the Keepo is theirs when it connected it)
    const backToKeepo = !!site && site === appState.editingSiteId;
    navigate(backToKeepo ? "/settings/custom-domain" : "/domains/mine", { state: { justBought: domain } });
  };

  // Back from a bank's 3D Secure page (the wallets redirect there when the bank asks): Stripe adds how it went
  // to the address. Paid: the same as paying here (confirm it, then My domains); otherwise say so
  const redirectStatus = query.get("redirect_status");
  const handledRedirect = useRef(false);
  useEffect(() => {
    if (!redirectStatus || !domain || handledRedirect.current) {
      return;
    }
    handledRedirect.current = true;
    if (redirectStatus === "succeeded") {
      setSubmitting(true);
      finish();
    } else {
      setSubmitError(t(`${prefix}.errors.generic`));
    }
    // Only on arrival
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!domain) {
    return <Navigate to="/domains" replace />;
  }

  const formatBRL = (value: number) => formatMoney(Math.round(value * 100), "brl", i18n.language);
  const validUntil = formatLocaleDate(dayjs().add(years, "year"), i18n.language);

  const back = (
    <TopLinks>
      <button type="button" onClick={() => navigate(site ? `/domains?site=${encodeURIComponent(site)}` : "/domains")}>
        <ChevronLeftIcon width={10} height={10} color="currentColor" />
        {t("pages.domains.title")}
      </button>
    </TopLinks>
  );

  if (price === undefined) {
    return (
      <Container>
        <Layout style={{ alignItems: "center" }}>
          <KeepoLoader size={32} color="var(--color-neutral-white)" />
        </Layout>
      </Container>
    );
  }

  if (price === "error") {
    return (
      <Container>
        {back}
        <Layout>
          <Header>
            <h1>{t(`${prefix}.loadError.title`)}</h1>
          </Header>
          <p style={{ color: "var(--color-neutral-150)" }}>{t(`${prefix}.loadError.text`)}</p>
          <Submit>
            <button type="button" className="cta" onClick={loadPrice}>
              {t(`${prefix}.loadError.retry`)}
            </button>
          </Submit>
        </Layout>
      </Container>
    );
  }

  if (price === null) {
    return (
      <Container>
        {back}
        <Layout>
          <Header>
            <h1>{t(`${prefix}.unavailable.title`, { domain })}</h1>
          </Header>
          <p style={{ color: "var(--color-neutral-150)" }}>{t(`${prefix}.unavailable.text`)}</p>
        </Layout>
      </Container>
    );
  }

  const cycles = cyclesOf(price);
  // A cycle not sold (an old link): the first one
  const cycle = cycles.find(item => item.years === years) || cycles[0];
  const total = formatBRL(cycle.price);

  // The details registering needs (the API checks them again)
  const validate = () => {
    const errors = validateBilling(owner, owner.country === "BR" ? "address" : "none");
    const contactInvalid = {
      email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim()),
      phone: contact.phone.replace(/\D/g, "").length < 8,
    };
    setOwnerErrors(errors);
    setContactErrors(contactInvalid);
    const nextCardErrors = method === "card" ? validateCard(card) : {};
    setCardErrors(nextCardErrors);
    if (!method) {
      setMethodMissing(true);
    }
    return (
      !!method &&
      !Object.keys(errors).length &&
      !contactInvalid.email &&
      !contactInvalid.phone &&
      !Object.keys(nextCardErrors).length
    );
  };

  // The API refused the CPF/CNPJ: the field says so
  const documentRejected = (error: unknown) => {
    if (!isApiError(error, "INVALID_DOCUMENT")) {
      return false;
    }
    setOwnerErrors(current => ({ ...current, document: "document" }));
    return true;
  };

  const orderError = (error: unknown) =>
    isApiError(error, DOMAIN_ORDER_ERRORS.taken)
      ? t(`${prefix}.errors.taken`)
      : isApiError(error, DOMAIN_ORDER_ERRORS.notSold)
        ? t(`${prefix}.errors.notSold`)
        : isApiError(error, DOMAIN_ORDER_ERRORS.plusRequired)
          ? t(`${prefix}.errors.plusRequired`)
          : isApiError(error, DOMAIN_ORDER_ERRORS.unavailable)
            ? t(`${prefix}.errors.unavailable`)
            : t(`${prefix}.errors.generic`);

  const createOrder = (payWith: "card" | "pix") =>
    createDomainOrder({
      domain,
      years: cycle.years,
      method: payWith,
      siteId: targetKeepo?.siteId,
      owner: { ...owner, email: contact.email.trim(), phone: contact.phone.trim() },
    });

  const submitHandler = async () => {
    setSubmitError(undefined);
    if (!validate() || (method !== "card" && method !== "pix")) {
      return;
    }
    setSubmitting(true);
    try {
      const order = await createOrder(method);
      if (method === "pix") {
        setPixOrder(order);
        return;
      }
      const cardElement = elements?.getElement(CardNumberElement);
      if (!stripe || !cardElement || !order.clientSecret) {
        setSubmitError(t(`${prefix}.errors.generic`));
        return;
      }
      const { error } = await stripe.confirmCardPayment(order.clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: { name: card.name.trim() || owner.name.trim(), email: contact.email.trim() },
        },
      });
      if (error) {
        setSubmitError(error.message || t(`${prefix}.errors.generic`));
        return;
      }
      await finish();
    } catch (error) {
      setSubmitError(documentRejected(error) ? undefined : orderError(error));
    } finally {
      setSubmitting(false);
    }
  };

  // A new Pix, the shown one expired (a new order: the old one is canceled when its Pix expires)
  const renewPix = async () => {
    setSubmitting(true);
    try {
      setPixOrder(await createOrder("pix"));
    } catch (error) {
      setPixOrder(undefined);
      setSubmitError(documentRejected(error) ? undefined : orderError(error));
    } finally {
      setSubmitting(false);
    }
  };

  if (pixOrder) {
    return (
      <Container>
        {back}
        <Content>
          <PixPayment
            key={pixOrder.paymentIntentId}
            order={pixOrder}
            total={formatMoney(pixOrder.amount, "brl", i18n.language)}
            onRenew={renewPix}
            onPaid={finish}
          />
        </Content>
        {isSubmitting && <LoadingModal />}
      </Container>
    );
  }

  const summary = (
    <Summary $open={isSummaryOpen} aria-label={t(`${checkoutPrefix}.summary.title`)}>
      <button
        type="button"
        className="toggle"
        aria-expanded={isSummaryOpen}
        onClick={() => setSummaryOpen(!isSummaryOpen)}>
        <span className="label">
          {isSummaryOpen ? t(`${checkoutPrefix}.summary.hide`) : t(`${checkoutPrefix}.summary.show`)}
          <ChevronRightIcon width={10} height={10} color="currentColor" />
        </span>
        <b>
          <small>{t("pages.domains.cycles.years", { count: cycle.years })} · </small>
          {total}
        </b>
      </button>

      <div className="details">
        <div className="keepo">
          <GlobeIcon size={18} />
          <span>{domain}</span>
        </div>
        <dl>
          {targetKeepo && (
            <div>
              <dt>{t(`${prefix}.order.opensAt`)}</dt>
              <dd>{`${targetKeepo.url}.keepo.bio`}</dd>
            </div>
          )}
          <div>
            <dt>{t(`${prefix}.order.registration`, { count: cycle.years })}</dt>
            <dd>{formatBRL(cycle.fullPrice)}</dd>
          </div>
          {cycle.fullPrice > cycle.price && (
            <div className="saving">
              <dt>{t(`${prefix}.order.discount`, { count: cycle.years })}</dt>
              <dd>-{formatBRL(cycle.fullPrice - cycle.price)}</dd>
            </div>
          )}
          <div className="saving">
            <dt>{t(`${prefix}.order.privacy`)}</dt>
            <dd>{t(`${prefix}.order.free`)}</dd>
          </div>
          <div className="saving">
            <dt>{t(`${prefix}.order.emailForwarding`)}</dt>
            <dd>{t(`${prefix}.order.free`)}</dd>
          </div>
        </dl>
        <p className="total">
          <span>{t(`${checkoutPrefix}.summary.total`)}</span>
          <b>{total}</b>
        </p>
        <p className="renewal">
          {t(`${prefix}.order.validUntil`, { date: validUntil })}{" "}
          {t(`${prefix}.order.renewsAt`, { value: formatBRL(price.renew) })}
        </p>
      </div>
    </Summary>
  );

  const form = (
    <Main>
      <section>
        <h2>{t("pages.domains.cycles.label")}</h2>
        <CycleOptions cycles={cycles} selected={cycle.years} onSelect={setYears} formatBRL={formatBRL} />
      </section>

      <section>
        <h2>{t(`${prefix}.owner.title`)}</h2>
        {/* The form groups' card (as the billing details'): why it's asked, then the fields */}
        <Panel>
          <p className="intro">{t(`${prefix}.owner.hint`)}</p>
          {/* E-mail and phone right under the name and CPF, before the address */}
          <BillingDetails
            value={owner}
            onChange={setOwner}
            errors={ownerErrors}
            withBrazilFields
            afterDocument={
              <>
                <Field
                  label={t(`${prefix}.owner.email`)}
                  error={contactErrors.email ? t(`${prefix}.errors.email`) : undefined}
                  fullOnMobile>
                  <input
                    type="email"
                    autoComplete="email"
                    value={contact.email}
                    onChange={e => setContact({ ...contact, email: e.target.value })}
                  />
                </Field>
                <Field
                  label={t(`${prefix}.owner.phone`)}
                  error={contactErrors.phone ? t(`${prefix}.errors.phone`) : undefined}
                  fullOnMobile>
                  <input
                    type="tel"
                    autoComplete="tel"
                    placeholder="(48) 99999-0000"
                    value={contact.phone}
                    onChange={e => setContact({ ...contact, phone: formatPhone(e.target.value, owner.country) })}
                  />
                </Field>
              </>
            }
          />
        </Panel>
      </section>

      <section>
        <h2>{t(`${checkoutPrefix}.methods.label`)}</h2>
        {/* No boleto for domains; the wallets only where the device has them */}
        <PaymentMethods
          availableWallets={availableWallets}
          walletFields={wallet => (
            <WalletPaymentButton
              wallet={wallet}
              amount={Math.round(cycle.price * 100)}
              validate={validate}
              // The wallets pay with the card inside them: a card order
              createPayment={() => createOrder("card")}
              onStart={() => setSubmitError(undefined)}
              onConfirm={() => setSubmitting(true)}
              onCancel={() => setSubmitting(false)}
              onPaid={finish}
              onError={message => {
                setSubmitting(false);
                setSubmitError(message || t(`${prefix}.errors.generic`));
              }}
            />
          )}
          isBrazil={owner.country === "BR"}
          isBoletoBlocked
          method={method}
          onMethodChange={next => {
            setMethod(next);
            setMethodMissing(false);
          }}
          disabled={isSubmitting}
          card={card}
          onCardChange={next => {
            setCard(next);
            if (Object.keys(cardErrors).length) {
              setCardErrors(validateCard(next));
            }
          }}
          cardErrors={cardErrors}
          captions={{ card: t(`${prefix}.methods.cardCaption`) }}
          info={{
            pix: t(`${prefix}.methods.pix`),
            applePay: t(`${prefix}.methods.wallet`, { wallet: "Apple Pay" }),
            googlePay: t(`${prefix}.methods.wallet`, { wallet: "Google Pay" }),
          }}
        />
        {isMethodMissing && <p className="method-error">{t(`${checkoutPrefix}.methods.missing`)}</p>}
      </section>

      <WalletProbe amount={Math.round(cycle.price * 100)} oneTime onAvailable={setAvailableWallets} />

      <Submit>
        {/* The wallets pay with their own button, inside their item */}
        {!isWallet && (
          <FireCta>
            <span className="fire-halo" />
            <span className="fire-ring" />
            <button
              type="button"
              className="cta"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
              onClick={submitHandler}>
              {/* While processing, Keepo's loading; the label stays for screen readers */}
              {isSubmitting
                ? t(`${checkoutPrefix}.submit.processing`)
                : t(`${prefix}.submit.${method === "pix" ? "pix" : "card"}`, { value: total })}
            </button>
          </FireCta>
        )}
        {submitError && (
          <p className="error" role="alert">
            {submitError}
          </p>
        )}
        <p className="legal">
          {t(`${prefix}.legal`)}
          <br />
          {t(`${prefix}.legalOwner`)}
        </p>
        <p className="secure">
          <LockIcon />
          {t("pages.subscription.paymentInfo")}
          <StripeIcon />
        </p>
      </Submit>
    </Main>
  );

  return (
    <Container>
      {back}
      <Layout>
        <Header>
          <h1>{t(`${prefix}.title`)}</h1>
        </Header>

        <Lock disabled={isSubmitting}>
          {summary}
          {form}
        </Lock>
      </Layout>
      {isSubmitting && <LoadingModal />}
    </Container>
  );
};

export default DomainCheckoutPage;
